/**
 * Screen 30 (Chats inbox) + Screen 32 (Message requests) — same page,
 * two tabs. Visual structure mirrors Triviana_Wireframes.html screens
 * 30 & 32: an appbar with a settings ic on the right, pill tabs, then
 * per-row cards with avatar + last-message preview + unread pill.
 *
 * A hash suffix picks the initial tab: /messages → chats,
 * /messages/requests → requests. `renderMessageRequests` calls into
 * this file with the requests tab pre-selected.
 */

import { api } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { I, icon } from '../components/icons.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import {
  acceptRequest,
  blockUser,
  declineRequest,
  fetchConversations,
  fetchRequests,
  seedChatBadgeFromInbox,
} from '../lib/messaging.js';

const TAB_CHATS = 'chats';
const TAB_REQUESTS = 'requests';
const SEARCH_DEBOUNCE_MS = 250;


function backLink() {
  return `<a href="/" data-link class="appbar-back" aria-label="Back">‹</a>`;
}


function shell(activeTab) {
  const settingsIc = `<a href="/chat-privacy" data-link class="ic" style="cursor:pointer;text-decoration:none;" aria-label="Chat privacy">${icon(I.lock, { size: 17 })}</a>`;
  return `
    <div class="appbar">${backLink()}<div class="appbar-title">Messages</div><div class="appbar-spacer"></div>${settingsIc}</div>
    <div class="pad">
      <div class="row" style="gap:8px;margin-bottom:11px;">
        <span class="pill ${activeTab === TAB_CHATS ? 'on' : ''}" data-tab="${TAB_CHATS}" style="flex:1;text-align:center;cursor:pointer;">Chats</span>
        <span class="pill ${activeTab === TAB_REQUESTS ? 'on' : ''}" data-tab="${TAB_REQUESTS}" style="flex:1;text-align:center;cursor:pointer;">
          Requests
          <span id="requests-badge" class="tag" style="background:#3A2F5F;color:#B7A6FF;border:1px solid #4A3C77;margin-left:5px;padding:3px 7px;display:none;"></span>
        </span>
      </div>

      <div class="row" id="search-bar" style="gap:8px;background:#1C1730;border:1px solid #2E2748;border-radius:12px;padding:8px 12px;margin-bottom:11px;">
        <span style="color:var(--mute);flex:none;">${icon(I.search, { size: 15 })}</span>
        <input type="text" id="chat-search" placeholder="Search a friend to message…"
          autocomplete="off"
          style="flex:1;background:transparent;border:none;outline:none;color:var(--ink);font-size:13px;font-weight:500;font-family:inherit;" />
        <button type="button" id="chat-search-clear" aria-label="Clear"
          style="background:transparent;border:none;color:var(--mute);cursor:pointer;padding:0 4px;font-size:16px;display:none;">×</button>
      </div>
      <div id="search-results" style="margin-bottom:11px;display:none;"></div>

      <div id="messages-content">
        <div class="muted center" style="padding:24px;font-size:12px;">Loading…</div>
      </div>
    </div>
  `;
}


function initials(name) {
  return (name || '?')[0].toUpperCase();
}


function chatRowHtml(item) {
  const peer = item.peer || {};
  const username = peer.username || 'Player';
  const preview = item.last_message ? escape(item.last_message.body) : '';
  const unread = item.unread_count || 0;
  const timestamp = item.last_message_at
    ? formatCompactTime(item.last_message_at)
    : '';
  const previewColor = unread > 0 ? '#D6D0EA' : 'var(--mute)';
  const pendingTag = item.is_request
    ? `<span class="tag" style="background:#3A2F5F;color:#B7A6FF;border:1px solid #4A3C77;padding:3px 7px;font-size:9px;margin-left:6px;">Pending</span>`
    : '';
  return `
    <div class="card" style="padding:11px;cursor:pointer;" data-conv="${escape(item.id)}">
      <div class="row">
        <div style="position:relative;">
          <div class="av" style="width:42px;height:42px;display:flex;align-items:center;justify-content:center;font-size:16px;font-weight:700;color:#fff;background:#2A2140;">${escape(initials(username))}</div>
        </div>
        <div class="grow" style="min-width:0;">
          <div class="row" style="gap:6px;">
            <b style="font-size:14px;color:#fff;font-weight:600;">${escape(username)}</b>${pendingTag}
          </div>
          <div style="font-size:11.5px;color:${previewColor};font-weight:500;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${preview}</div>
        </div>
        <div style="text-align:right;flex:none;">
          <div style="font-size:10px;color:var(--mute);font-weight:500;">${escape(timestamp)}</div>
          ${
            unread > 0
              ? `<span style="display:inline-block;margin-top:5px;min-width:19px;height:19px;border-radius:10px;background:var(--teal);color:#04211C;font-size:10px;line-height:19px;text-align:center;font-weight:700;font-family:var(--font-display);padding:0 5px;">${unread}</span>`
              : ''
          }
        </div>
      </div>
    </div>
  `;
}


function searchResultHtml(row) {
  const u = row.user || {};
  const name = u.username || u.display_name || 'Player';
  const rel = row.relationship || 'none';
  const relTag = (() => {
    if (rel === 'friend')
      return `<span class="tag" style="background:#0F352A;color:#5FE0BE;border:1px solid #1E6E5C;padding:3px 7px;font-size:9px;">friend</span>`;
    if (rel === 'self') return '';
    if (rel === 'blocked' || rel === 'blocked_by')
      return `<span class="tag" style="background:#3A1528;color:#FF8FB6;border:1px solid #5A2440;padding:3px 7px;font-size:9px;">blocked</span>`;
    return '';
  })();
  const hidden = rel === 'self' || rel === 'blocked' || rel === 'blocked_by';
  if (hidden) return '';
  return `
    <div class="card" style="padding:10px;cursor:pointer;" data-search-user="${escape(u.id || '')}">
      <div class="row">
        <div class="av" style="width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;color:#fff;background:#2A2140;">${escape(initials(name))}</div>
        <div class="grow">
          <div class="row" style="gap:6px;">
            <b style="font-size:13.5px;color:#fff;font-weight:600;">${escape(name)}</b>${relTag}
          </div>
        </div>
        <span class="btn teal sm" style="margin:0;width:auto;padding:7px 12px;">${icon(I.chat, { size: 13 })}</span>
      </div>
    </div>
  `;
}


function requestRowHtml(item) {
  const from = item.from_user || {};
  const preview = item.preview ? escape(item.preview.body) : '';
  const timeAgo = item.created_at ? formatAgo(item.created_at) : '';
  return `
    <div class="card" style="padding:12px;" data-request-id="${item.id}" data-from-id="${escape(from.id || '')}">
      <div class="row">
        <div class="av" style="width:40px;height:40px;display:flex;align-items:center;justify-content:center;font-size:15px;font-weight:700;color:#fff;background:#2A2140;">${escape(initials(from.username))}</div>
        <div class="grow">
          <div class="row" style="gap:6px;">
            <b style="font-size:14px;color:#fff;font-weight:600;">${escape(from.username || 'Player')}</b>
            <span class="tag" style="background:#2A2140;color:#9A93BE;border:1px solid #3A2F5F;padding:3px 7px;font-size:9px;">not a friend</span>
          </div>
          <div style="font-size:10px;color:var(--mute);font-weight:500;margin-top:1px;">${escape(timeAgo)} · 1 message</div>
        </div>
      </div>
      <div style="margin-top:9px;background:#1C1730;border:1px solid #2E2748;border-radius:12px;padding:10px 12px;font-size:12.5px;color:#D6D0EA;font-weight:500;line-height:1.5;">${preview}</div>
      <div class="row" style="gap:8px;margin-top:10px;">
        <button type="button" data-action="accept" class="btn teal sm" style="flex:1;width:auto;margin:0;">Accept</button>
        <button type="button" data-action="decline" class="btn ghost sm" style="flex:1;width:auto;margin:0;">Decline</button>
        <button type="button" data-action="block" class="btn ghost sm" style="width:auto;margin:0;color:#FF8FB6;border-color:#5A2440;">${icon(I.ban || I.close, { size: 13 })}</button>
      </div>
    </div>
  `;
}


function formatCompactTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d`;
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}


function formatAgo(iso) {
  if (!iso) return '';
  const t = formatCompactTime(iso);
  return t ? `${t} ago` : '';
}


async function paintChats(container) {
  try {
    const res = await fetchConversations();
    seedChatBadgeFromInbox(res.items);
    if (!res.items.length) {
      container.innerHTML = `
        <div class="card center" style="padding:24px;">
          <b style="font-size:13px;color:#fff;font-weight:600;">No conversations yet</b>
          <div class="muted" style="font-size:11px;margin-top:5px;">Message a friend from their profile to get started.</div>
        </div>
      `;
      return;
    }
    container.innerHTML = res.items.map(chatRowHtml).join('');
    container.querySelectorAll('[data-conv]').forEach((el) => {
      el.addEventListener('click', () => {
        navigate(`/messages/${encodeURIComponent(el.dataset.conv)}`);
      });
    });
  } catch (err) {
    container.innerHTML = `
      <div class="card center" style="padding:20px;">
        <b style="font-size:12.5px;color:#F7C667;font-weight:600;">Couldn't load conversations</b>
        <div class="muted" style="font-size:11px;margin-top:4px;">${escape(err.message || 'Try again in a moment.')}</div>
      </div>
    `;
    console.error('inbox load failed', err);
  }
}


async function paintRequests(container, badgeEl) {
  try {
    const res = await fetchRequests();
    updateBadge(badgeEl, res.items.length);
    if (!res.items.length) {
      container.innerHTML = `
        <div class="card center" style="padding:24px;">
          <b style="font-size:13px;color:#fff;font-weight:600;">No requests right now</b>
          <div class="muted" style="font-size:11px;margin-top:5px;">People who aren't your friends will appear here when they send a first message.</div>
        </div>
        <div class="card" style="border-color:#3A2F5F;background:#1A1530;padding:11px;margin-top:11px;">
          <div class="row">
            <div class="ic" style="background:#2A2140;color:#B7A6FF;">${icon(I.lock, { size: 16 })}</div>
            <div class="grow">
              <b style="font-size:12.5px;color:#fff;font-weight:600;">Quiet by design</b>
              <div class="muted" style="font-size:11px;font-weight:500;margin-top:1px;">Requests don't ring like normal chats, and declining never tells the sender.</div>
            </div>
          </div>
        </div>
      `;
      return;
    }
    container.innerHTML = res.items.map(requestRowHtml).join('');
    container.querySelectorAll('.card[data-request-id]').forEach((row) => {
      row.querySelectorAll('[data-action]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const action = btn.dataset.action;
          const reqId = row.dataset.requestId;
          const fromId = row.dataset.fromId;
          btn.disabled = true;
          try {
            if (action === 'accept') {
              const r = await acceptRequest(reqId);
              toast('Request accepted');
              navigate(`/messages/${r.conversation_id}`);
              return;
            }
            if (action === 'decline') {
              await declineRequest(reqId);
              toast('Request declined');
              row.remove();
            } else if (action === 'block') {
              if (!confirm('Block this user? They will not be able to reach you.')) {
                btn.disabled = false;
                return;
              }
              await blockUser(fromId);
              toast('User blocked');
              row.remove();
            }
          } catch (err) {
            toast(err.message || 'Action failed');
            btn.disabled = false;
          }
        });
      });
    });
  } catch (err) {
    container.innerHTML = `
      <div class="card center" style="padding:20px;">
        <b style="font-size:12.5px;color:#F7C667;font-weight:600;">Couldn't load requests</b>
        <div class="muted" style="font-size:11px;margin-top:4px;">${escape(err.message || 'Try again in a moment.')}</div>
      </div>
    `;
    console.error('requests load failed', err);
  }
}


function updateBadge(badgeEl, count) {
  if (!badgeEl) return;
  if (count > 0) {
    badgeEl.textContent = String(count);
    badgeEl.style.display = '';
  } else {
    badgeEl.style.display = 'none';
  }
}


async function renderTab(root, tab) {
  const content = root.querySelector('#messages-content');
  const badge = root.querySelector('#requests-badge');
  if (!content) return;
  content.innerHTML = `<div class="muted center" style="padding:24px;font-size:12px;">Loading…</div>`;
  if (tab === TAB_REQUESTS) {
    history.replaceState(null, '', '/messages/requests');
    await paintRequests(content, badge);
  } else {
    history.replaceState(null, '', '/messages');
    await paintChats(content);
    // Prime the requests badge from the Chats tab too.
    fetchRequests()
      .then((res) => updateBadge(badge, res.items.length))
      .catch(() => {});
  }
}


function wireSearch(root) {
  const input = root.querySelector('#chat-search');
  const clear = root.querySelector('#chat-search-clear');
  const results = root.querySelector('#search-results');
  const content = root.querySelector('#messages-content');
  let debounce = null;
  let lastQuery = '';

  async function run(query) {
    if (!query || query.length < 2) {
      results.style.display = 'none';
      results.innerHTML = '';
      content.style.display = '';
      return;
    }
    content.style.display = 'none';
    results.style.display = '';
    results.innerHTML = `<div class="muted center" style="padding:16px;font-size:11.5px;">Searching…</div>`;
    try {
      const rows = await api(
        `/friends/search?q=${encodeURIComponent(query)}`,
      );
      const html = (rows || [])
        .map(searchResultHtml)
        .filter(Boolean)
        .join('');
      if (!html) {
        results.innerHTML = `<div class="muted center" style="padding:16px;font-size:11.5px;">No matches for "${escape(query)}".</div>`;
        return;
      }
      results.innerHTML = html;
      results.querySelectorAll('[data-search-user]').forEach((el) => {
        el.addEventListener('click', () => {
          const uid = el.dataset.searchUser;
          if (uid) navigate(`/messages/new/${encodeURIComponent(uid)}`);
        });
      });
    } catch (err) {
      results.innerHTML = `<div class="muted center" style="padding:16px;font-size:11.5px;">Couldn't search — try again.</div>`;
      console.error('search failed', err);
    }
  }

  input.addEventListener('input', () => {
    const q = input.value.trim();
    clear.style.display = q ? '' : 'none';
    if (q === lastQuery) return;
    lastQuery = q;
    if (debounce) clearTimeout(debounce);
    debounce = setTimeout(() => run(q), SEARCH_DEBOUNCE_MS);
  });
  clear.addEventListener('click', () => {
    input.value = '';
    lastQuery = '';
    clear.style.display = 'none';
    run('');
    input.focus();
  });
}


async function _render(root, initialTab) {
  if (!auth.isAuthenticated()) {
    navigate('/onboarding');
    return;
  }
  root.innerHTML = shell(initialTab);
  root.querySelectorAll('[data-tab]').forEach((btn) => {
    btn.addEventListener('click', () => {
      root.querySelectorAll('[data-tab]').forEach((b) => b.classList.remove('on'));
      btn.classList.add('on');
      renderTab(root, btn.dataset.tab);
    });
  });
  wireSearch(root);
  await renderTab(root, initialTab);
}


export function renderMessagesInbox(root) {
  return _render(root, TAB_CHATS);
}

export function renderMessageRequests(root) {
  return _render(root, TAB_REQUESTS);
}
